import mixin from '@/helpers/mixins';
import { deepClone } from '@/helpers/utils';
import Store from '@/store/index';
import Vue from 'vue';
import { getCasePartyId, getRepresentativeByRelatedObjectKey, isProsecutorOrDefendant, isRepresentative } from '../helpers/utils';
import { initialData } from './state';
import { casePartyRoleEnum } from '../../../helpers/enum';
import { uuidGenerator } from '@/helpers/utils';

export default {
  updateCurrentStep(state, step) {
    state.currentStep = step || initialData().currentStep;
  },
  updateCase(state, caseInfo) {
    Vue.set(state.lawsuitRequest, 'case', caseInfo);
  },
  setOriginalData(state, data) {
    Vue.set(state, 'originalData', {
      ...state.originalData,
      ...data,
    });
  },
  updateCaseParties(state, caseParties) {
    Vue.set(state.lawsuitRequest, 'caseParties', caseParties);
  },
  updateAuditFields(state, auditFields) {
    for (const key in auditFields)
      if (Object.hasOwnProperty.call(auditFields, key))
        Vue.set(state.lawsuitRequest.auditFields, key, auditFields[key]);
  },
  setRelatedCase(state, relatedCase) {
    Vue.set(state.lawsuitRequest, 'relatedCases', relatedCase);
  },
  updateLawsuitRequestAttachments(state, attachments) {
    Vue.set(state.lawsuitRequest, 'requestAttachments', attachments);
  },
  updateCaseRelatedCaseCodeByReportNumber(state, relatedCaseCode) {
    Vue.set(state.lawsuitRequest.case, 'relatedCaseCodeByReportNumber', relatedCaseCode);
  },
  updateSubMode(state, mode) {
    state.subMode = mode;
  },
  updateSelectedCaseParty(state, caseParty) {
    state.selectedCaseParty = deepClone(caseParty);
  },
  updateSelectedCasePartyObjectKey(state, casePartyObjectKey) {
    Vue.set(state.selectedCaseParty, 'objectKey', casePartyObjectKey);
  },
  updateSelectedCasePartyRepresentative(state, casePartyRepresentative) {
    state.selectedCaseParty = deepClone(casePartyRepresentative);
  },
  updateSelectedCasePartyRepresentativeObjectKey(state, casePartyRepresentativeObjectKey) {
    const relatedCasePartyIndex = state.selectedCaseParty.relatedPartiesInfo.findIndex(
      cp => cp.casePartyObjectKey === state.selectedCaseParty.casePartyObjectKey
    );
    Vue.set(
      state.selectedCaseParty.relatedPartiesInfo[relatedCasePartyIndex],
      'objectKey',
      casePartyRepresentativeObjectKey
    );
  },
  saveNewCaseParties(state, caseParties) {
    caseParties.forEach((cp) => {
      mixin.$push(state.lawsuitRequest.caseParties, cp);
    });
  },
  saveNewCaseParty(state, caseParty) {
    // always save the case party itself (including a deceased/inheritor defendant),
    // otherwise it would be missing from validations right after being added.
    mixin.$push(state.lawsuitRequest.caseParties, caseParty);
  },
  saveNewCasePartyRepresentative(state, casePartyRepresentative) {
    mixin.$push(state.lawsuitRequest.caseParties, casePartyRepresentative);
  },
  saveExistCasePartyRepresentativeAsCaseParty(state, caseParty) {
    const selectedCasePartyIndex = state.lawsuitRequest.caseParties.findIndex(
      cp => getCasePartyId(cp).uniqueId === getCasePartyId(caseParty).uniqueId
    );
    const relatedPartiesInfo = state.lawsuitRequest.caseParties[selectedCasePartyIndex].relatedPartiesInfo;
    Vue.set(state.lawsuitRequest.caseParties, selectedCasePartyIndex, {
      ...caseParty,
      relatedPartiesInfo,
    });
  },
  saveExistCaseParty(state, caseParty) {
    const selectedCasePartyIndex = state.lawsuitRequest.caseParties.findIndex(
      cp => cp.objectKey === caseParty.objectKey
    );
    Vue.set(state.lawsuitRequest.caseParties, selectedCasePartyIndex, caseParty);

    Vue.set(state.lawsuitRequest.caseParties, selectedCasePartyIndex, caseParty);
  },
  saveExistCasePartyRepresentative(state, casePartyRepresentative) {
    const selectedCasePartyRepresentativeIndex = state.lawsuitRequest.caseParties.findIndex(cp =>
      cp.relatedPartiesInfo.some(rp =>
        casePartyRepresentative.relatedPartiesInfo.some(crp => rp.objectKey === crp.objectKey)
      )
    );
    Vue.set(
      state.lawsuitRequest.caseParties,
      selectedCasePartyRepresentativeIndex,
      casePartyRepresentative
    );
  },
  saveExistCasePartyAsRepresentative(state, casePartyRepresentative) {
    const relatedCaseParty = casePartyRepresentative.relatedPartiesInfo.find(
      rp => rp.casePartyObjectKey === casePartyRepresentative.casePartyObjectKey
    );
    const existsCaseParty = state.lawsuitRequest.caseParties.find(
      cp => getCasePartyId(cp).uniqueId === getCasePartyId(casePartyRepresentative).uniqueId
    );

    if (existsCaseParty && relatedCaseParty) {
      mixin.$push(existsCaseParty.relatedPartiesInfo, relatedCaseParty);
    }
  },
  removeCaseParty(state, payload) {
    const casePartyIndex = state.lawsuitRequest.caseParties.findIndex(
      cp => cp.objectKey === payload.objectKey
    );

    if (isRepresentative(state.lawsuitRequest.caseParties[casePartyIndex])) {
      Vue.set(state.lawsuitRequest.caseParties[casePartyIndex], 'casePartyRole', 0);
    } else {
      const casePartyToDelete = state.lawsuitRequest.caseParties[casePartyIndex];
      const relatedDeceasedCaseParties = casePartyToDelete?.socialId
        ? state.lawsuitRequest.caseParties.filter(
          x => x.relatedDeceasedSocialId === casePartyToDelete.socialId
        )
        : [];

      relatedDeceasedCaseParties.forEach((cp) => {
        const index = state.lawsuitRequest.caseParties.findIndex(
          party => party.objectKey === cp.objectKey
        );
        if (index !== -1) {
          Vue.delete(state.lawsuitRequest.caseParties, index);
        }
      });

      Vue.delete(state.lawsuitRequest.caseParties, casePartyIndex);
    }

    removeCasePartyChild(state.lawsuitRequest.caseParties, payload.objectKey);
  },
  removeCasePartyRepresentative(state, payload) {
    const casePartyRepresentative = getRepresentativeByRelatedObjectKey(
      state.lawsuitRequest.caseParties,
      payload.objectKey
    );
    const casePartyRepresentativeIndex = state.lawsuitRequest.caseParties.findIndex(cp =>
      cp.relatedPartiesInfo.some(rp =>
        casePartyRepresentative.relatedPartiesInfo.some(crp => rp.objectKey === crp.objectKey)
      )
    );

    if (
      isProsecutorOrDefendant(casePartyRepresentative) ||
      casePartyRepresentative.relatedPartiesInfo.length > 1
    ) {
      const relatedCasePartyIndex = casePartyRepresentative.relatedPartiesInfo.findIndex(
        rp => rp.objectKey === payload.objectKey
      );
      Vue.delete(
        state.lawsuitRequest.caseParties[casePartyRepresentativeIndex].relatedPartiesInfo,
        relatedCasePartyIndex
      );
    } else {
      Vue.delete(state.lawsuitRequest.caseParties, casePartyRepresentativeIndex);
    }

    removeCasePartyChild(state.lawsuitRequest.caseParties, payload.objectKey);
  },
  resetCasePartyForm(state) {
    state.subMode = null;
    state.selectedCaseParty = initialData().selectedCaseParty;
    state.attachments = {
      // tODO: Remove this object
      data: {},
      viewAttachmentModal: {
        selectedAttachment: null,
        selectedAttachmentKey: null,
      },
      deleteAttachmentModal: {
        visibility: false,
        objectKey: null,
        attachmentKey: null,
      },
    };
  },
  resetLawsuitRequest(state) {
    state.lawsuitRequest = initialData().lawsuitRequest;
  },
  updateSubmittedRequestInfo(state, payload) {
    state.submittedRequestInfo = payload;
  },
  updateSubmittedRequestInfoObjectKey(state, objectKey) {
    Vue.set(state.submittedRequestInfo, 'objectKey', objectKey);
  },
  updateAttachments(state, attachments) {
    Vue.set(state.attachments, 'data', Object.assign({}, state.attachments.data, attachments));
  },
  setAttachments(state, attachments) {
    Vue.set(state.attachments, 'data', attachments);
  },
  deleteAttachment(state, attachmentKey) {
    const newAttachments = Object.keys(state.attachments.data)
      .reduce((att, currentKey) => {
        const attachments = att;
        if (currentKey !== attachmentKey) attachments[currentKey] = state.attachments.data[currentKey];
        return attachments;
      }, {});
    state.attachments.data = newAttachments;
  },
  updateAttachmentModal(state, { selectedAttachment, selectedAttachmentKey }) {
    const modal = state.attachments.viewAttachmentModal;
    state.attachments.viewAttachmentModal = {
      selectedAttachment:
        typeof selectedAttachment !== 'undefined' ? selectedAttachment : modal.selectedAttachment,
      selectedAttachmentKey:
        typeof selectedAttachmentKey !== 'undefined'
          ? selectedAttachmentKey
          : modal.selectedAttachmentKey,
    };
  },
  updateDeleteAttachmentModalVisibility(state, { visibility, objectKey, attachmentKey }) {
    state.attachments.deleteAttachmentModal = {
      visibility,
      objectKey: objectKey || null,
      attachmentKey: attachmentKey || null,
    };
  },
  updateCaseMovementReplyTypes(state, caseMovementReplyTypes) {
    state.caseMovementReplyTypes = caseMovementReplyTypes;
  },
  updateSimilarCases(state, similarCases) {
    state.similarCases = similarCases;
  },
  updateSimilarReconciliationRequests(state, SimilarReconciliationRequests) {
    state.similarReconciliationRequests = SimilarReconciliationRequests;
  },
  updateRelatedCases(state, relatedCases) {
    state.relatedCases = relatedCases;
  },
  updateCasePreviousReplies(state, previousReplies) {
    Vue.set(state.lawsuitRequest, 'previousReplies', previousReplies);
  },
  updateSimilarCasesModalVisibility(state, { visibility }) {
    state.similarCasesModal = { visibility };
  },
  updateReplyModalVisibility(state, { visibility }) {
    state.replyModal = { visibility };
  },
  updateCourts(state, courts) {
    Vue.set(state.lawsuitRequest, 'courts', courts);
  },
  updateLawsuitDynamicFormsModel(state, forms) {
    var lastForm = (forms || []).pop();
    Vue.set(state.lawsuitRequest.case, 'forms', lastForm ? [lastForm] : []);
  },
  updateLawsuitDynamicForms(state, flatDynamicFormsByName) {
    Vue.set(state, 'flatDynamicFormsByName', flatDynamicFormsByName);
  },
  setCaseTypesSearchResult(state, result) {
    Vue.set(state.lawsuitRequest, 'caseTypesSearchResult', result);
  },
  updateSimilarRequests(state, similarRequests) {
    state.similarRequests = similarRequests;
  },
  updateLawsuitRequiredAttachmentTypes(state, LawsuitRequiredAttachmentTypes) {
    Vue.set(state.lawsuitRequest, 'requiredAttachmentTypes', LawsuitRequiredAttachmentTypes);
  },
  updateLawsuitAttachmentTypes(state, LawsuitAttachmentTypes) {
    Vue.set(state.lawsuitRequest, 'attachmentTypes', LawsuitAttachmentTypes);
  },
  updateIsPledged(state, IsPledged) {
    state.isPledged = IsPledged;
  },
  updateRequestAttachmentAuditField(state, { requestAttachmentObjectKey, isAttachmentAudited }) {
    const attachment = state.attachments.data[requestAttachmentObjectKey];
    if (attachment) {
      Vue.set(attachment, 'isAttachmentAudited', isAttachmentAudited);
    }
  },
  resetCaseCourtId(state) {
    Vue.set(state.lawsuitRequest.case, 'courtId', null);
  },
  // صفات الاطراف
  updateActivePartyAttributes(state, list) {
    Vue.set(state, 'activePartyAttributes', list);
  },
  resetCaseParties(state) {
    state.lawsuitRequest.caseParties = [];
  },
  setPartyAttributesBasicInfo(state, data) {
    state.partyAttributesBasicInfo = data;
  },
  resetCasePartiesByObjectKeys(state, objectKeys) {
    objectKeys.forEach((key) => {
      const index = state.lawsuitRequest.caseParties.findIndex((cp) => cp.objectKey == key);
      if (index != -1) {
        Vue.delete(state.lawsuitRequest.caseParties, index);
        removeCasePartyChild(state.lawsuitRequest.caseParties, key);
      }
    });
  },
};

const removeCasePartyChild = (caseParties, objectKey) => {
  caseParties.forEach((cp, index) => {
    if (cp.relatedPartiesInfo.some(rp => rp.casePartyObjectKey === objectKey)) {
      if (cp.relatedPartiesInfo.length > 1 || isProsecutorOrDefendant(cp)) {
        const relatedCasePartyIndex = cp.relatedPartiesInfo.findIndex(
          rp => rp.casePartyObjectKey === objectKey
        );
        Vue.delete(caseParties[index].relatedPartiesInfo, relatedCasePartyIndex);
      } else {
        const casePartyObjectKey = caseParties[index].objectKey;
        Vue.delete(caseParties, index);
        removeCasePartyChild(caseParties, casePartyObjectKey);
      }
    }
  });
};